/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  ContactRestService,
  JobEstimateRestService,
  JobOrderDelivery,
  JobOrderMethod,
  JobOrderRestService,
} from '@gh/builder-shared-data';
import { FileListItem } from '@gh/common-internal-shared';
import { DictionaryService, InitDialogViewService } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { filter$, map$ } from '@gh/rx/operators';
import { first } from 'lodash';
import { Observable } from 'rxjs';
import {
  JobOrderDetails,
  jobOrderDetailsFromDto,
  jobOrderDetailsFromGeneratedDto,
  JobOrderDetailsMasterInfo,
  jobOrderDetailsToDto,
  JobOrderSupplier,
  jobOrderSupplierFromContactDto,
} from './job-order-details.model';

export interface JobOrderDetailsDialogConfig {
  allowSupplierChange: boolean;
  masterInfo: JobOrderDetailsMasterInfo;
  preferredSuppliers: JobOrderSupplier[];
  order: JobOrderDetails | Partial<JobOrderDetails>;

  findAllOrderAttachments(costCenterCode: number, orderNumber: number): Observable<FileListItem[]>;
}

@Injectable()
export class JobOrderDetailsBusinessService implements InitDialogViewService<JobOrderDetailsDialogConfig> {
  constructor(private jobOrderRest: JobOrderRestService,
              private jobEstimateRest: JobEstimateRestService,
              private contactRest: ContactRestService,
              private dictionaries: DictionaryService) {

  }

  initDialogView(route: DialogRouteSnapshot<JobOrderDetailsDialogConfig>): any {
    const { order } = route.input;

    return {
      deliveryDict: this.dictionaries.getByEnum(JobOrderDelivery),
      methodDict: this.dictionaries.getByEnum(JobOrderMethod),
      order: isNotNil(order.orderId)
        ? order
        : this.jobEstimateRest.generateOrders([order.costCenter!.id]).pipe(// tslint:disable-line:no-non-null-assertion
          map$(first),
          filter$(Boolean),
          map$((generatedOrder: any) => jobOrderDetailsFromGeneratedDto(order, generatedOrder))),
    };
  }

  findAllSuppliersByName(query: FilteredDataQuery<string>, excludedIds: ID[]): Observable<JobOrderSupplier[]> {
    return this.contactRest.findAllSuppliersAsDataSource({
      searchString: query.filter,
      excludedIds,
    }).select(query).pipe(map$(getSlicedData), map$((contacts) => contacts.map(jobOrderSupplierFromContactDto)));
  }

  save(jobId: ID, order: JobOrderDetails): Observable<JobOrderDetails> {
    return this.jobOrderRest.update(jobId, jobOrderDetailsToDto(order)).pipe(
      map$((dto) => jobOrderDetailsFromDto(dto, order.costCenter.name)));
  }
}
